/**
 * SolarSystemGenerator — procedural solar system: a central star anchor with
 * planets in distinct concentric orbits.
 *
 *  1. Central star anchor — always at (0,0); the render layer gives it a glow
 *     (Three.js PointLight + radial-gradient sprite) and draws it much larger
 *     than the planets.
 *  2. Procedural orbits — N = 3..8 planets, each on its own circular orbit whose
 *     radius r is strictly greater than the previous by at least MIN_ORBIT_GAP
 *     (no overlap). Positions use polar coords: x = r·cosθ, y = r·sinθ.
 *  3. Planet properties — a `type` (Rocky / Gas Giant / Ice / Volcanic / Oceanic)
 *     drives colour, size and ore yield; an `orbitalSpeed` revolves θ over time.
 *  4. Orbit paths — `orbitRadii` lets the renderer draw faint circular tracks.
 *
 * Planets carry the mining data (ore/oreMax + radius) so they double as the
 * mineable bodies the drones and hull laser target.
 */
import { Vector2 } from "@/math/Vector2";
import { makeRng } from "@/math/rng";
import { MOON_TURRET } from "@/config/balance";

export type PlanetType = "rocky" | "gas" | "ice" | "volcanic" | "oceanic";

/** A planet may host a service you fly to: trade hub, mechanical refit yard, or
 * a Space Tavern (recruit crew). */
export type PlanetService = "none" | "trade" | "mechanical" | "tavern";

export interface PlanetTypeDef {
  label: string;
  color: number; // "sprite" tint / fill
  radius: number; // body size (planets are far smaller than the star)
  oreMax: number; // resource yield
}

/** Type table — sprite tint, size, and resource yield per planet type. */
export const PLANET_TYPES: Record<PlanetType, PlanetTypeDef> = {
  rocky: { label: "Rocky", color: 0x9a6d4f, radius: 24, oreMax: 900 },
  gas: { label: "Gas Giant", color: 0xd7a24b, radius: 46, oreMax: 2400 },
  ice: { label: "Ice", color: 0x9fd8ff, radius: 30, oreMax: 1500 },
  volcanic: { label: "Volcanic", color: 0xff5a2a, radius: 27, oreMax: 1700 },
  oceanic: { label: "Oceanic", color: 0x3d7bff, radius: 33, oreMax: 1300 },
};

export interface Star {
  pos: Vector2; // always (0,0)
  radius: number;
  color: number;
  sprite: number; // 0..1 seed → star atlas frame
}

/** A visual moon shown orbiting a planet in the SYSTEM view (big moons only). */
export interface SystemMoon {
  idx: number; // matches the planet-layer moon index (moon_<idx>) for destruction sync
  angle: number;
  distance: number; // from the planet centre
  speed: number; // rad/s
  radius: number;
}

export interface Planet {
  id: string;
  type: PlanetType;
  angle: number; // current θ
  distance: number; // orbit radius r from the star
  orbitalSpeed: number; // rad/s (sign = orbit direction)
  radius: number; // body size
  color: number;
  sprite: number; // 0..1 seed → planet atlas frame
  pos: Vector2; // world position, recomputed each tick from (r, θ)
  ore: number; // mining data
  oreMax: number;
  service: PlanetService; // trade hub / mechanical refit yard / none
  rogue: boolean; // hostile "rogue" world — its moons carry turrets, richer ore
  moons: SystemMoon[]; // count matches the planet-layer moon count
}

/** Deterministic per-planet seed (shared with the planet-layer generator so the
 * displayed moon count matches what you find when you fly in). */
export function planetSeed(systemSeed: number, idx: number): number {
  return (systemSeed * 2654435761 + idx * 40503) >>> 0;
}

export interface SolarSystem {
  seed: number;
  star: Star;
  planets: Planet[];
  orbitRadii: number[]; // for drawing the faint orbital tracks
}

// --- tuning ---
const STAR_RADIUS = 96; // star is significantly larger than any planet
const FIRST_ORBIT = 260; // innermost orbit distance from the star
const MIN_ORBIT_GAP = 150; // minimum spacing between successive orbits
const ORBIT_JITTER = 130; // extra random spacing on top of the minimum

const ALL_TYPES = Object.keys(PLANET_TYPES) as PlanetType[];

export interface GenerateOptions {
  seed: number;
  maxPlanets?: number; // upper bound on planet count (min stays 3); grows per warp
  maxMoons?: number; // upper bound on big moons per planet; grows per warp
  warps?: number; // current jump count — gates rogue planets (from warp 7)
}

/** Initialise the star and place planets in their calculated orbits. */
export function generateSystem(opts: GenerateOptions): SolarSystem {
  const rng = makeRng(opts.seed);
  const maxPlanets = Math.max(3, opts.maxPlanets ?? 8);
  const maxMoons = Math.max(0, opts.maxMoons ?? 5);
  const star: Star = {
    pos: new Vector2(0, 0),
    radius: STAR_RADIUS,
    color: 0xffd27f,
    sprite: rng.next(),
  };

  const count = rng.int(3, maxPlanets);
  const planets: Planet[] = [];
  const orbitRadii: number[] = [];

  let r = FIRST_ORBIT;
  for (let i = 0; i < count; i++) {
    // Each orbit is at least MIN_ORBIT_GAP beyond the previous → no overlap.
    if (i > 0) r += MIN_ORBIT_GAP + rng.range(0, ORBIT_JITTER);

    const type = rng.pick(ALL_TYPES);
    const def = PLANET_TYPES[type];
    const angle = rng.range(0, Math.PI * 2);
    // Inner planets revolve faster (loose Keplerian feel); direction randomised.
    const dir = rng.next() < 0.5 ? 1 : -1;
    const orbitalSpeed = dir * (0.015 + 9 / r) * (0.6 + rng.next() * 0.7);

    // Moon count from the SAME seed the planet layer uses → they match in-world.
    const moonCount = makeRng(planetSeed(opts.seed, i)).int(0, maxMoons);
    const moons: SystemMoon[] = [];
    for (let j = 0; j < moonCount; j++) {
      moons.push({
        idx: j,
        angle: rng.range(0, Math.PI * 2),
        distance: def.radius + 16 + j * 11,
        speed: (rng.next() < 0.5 ? 1 : -1) * (0.35 + rng.next() * 0.5),
        radius: 3.5 + rng.next() * 2,
      });
    }

    planets.push({
      id: `planet_${i}`,
      type,
      angle,
      distance: r,
      orbitalSpeed,
      radius: def.radius,
      color: def.color,
      sprite: rng.next(),
      pos: new Vector2(Math.cos(angle) * r, Math.sin(angle) * r),
      ore: def.oreMax,
      oreMax: def.oreMax,
      service: "none",
      rogue: false,
      moons,
    });
    orbitRadii.push(r);
  }

  // Guarantee one mechanical refit yard and one trade hub per system; from a few
  // planets up, also a Space Tavern.
  if (planets.length >= 1) {
    const mIdx = rng.int(0, planets.length - 1);
    planets[mIdx]!.service = "mechanical";
    if (planets.length >= 2) {
      let tIdx = rng.int(0, planets.length - 1);
      if (tIdx === mIdx) tIdx = (tIdx + 1) % planets.length;
      planets[tIdx]!.service = "trade";
    }
    if (planets.length >= 3) {
      // Put the tavern on the first still-unassigned planet from a random start.
      let vIdx = rng.int(0, planets.length - 1);
      for (let g = 0; g < planets.length; g++) {
        if (planets[vIdx]!.service === "none") break;
        vIdx = (vIdx + 1) % planets.length;
      }
      if (planets[vIdx]!.service === "none") planets[vIdx]!.service = "tavern";
    }
  }

  // Rogue planets: from jump 7 on, some non-service worlds turn hostile — their
  // moons carry turrets and their resources are far richer. Service hubs stay safe.
  if ((opts.warps ?? 0) >= MOON_TURRET.fromWarp) {
    for (const p of planets) {
      if (p.service === "none" && rng.next() < MOON_TURRET.rogueChance) p.rogue = true;
    }
  }

  return { seed: opts.seed, star, planets, orbitRadii };
}

/** Advance every planet's θ and recompute its world position (call each frame). */
export function updateSystem(system: SolarSystem, dt: number): void {
  for (const p of system.planets) {
    p.angle += p.orbitalSpeed * dt;
    p.pos.set(Math.cos(p.angle) * p.distance, Math.sin(p.angle) * p.distance);
    for (const m of p.moons) m.angle += m.speed * dt;
  }
}

/**
 * SolarSystemManager — OO wrapper holding the star + Planet array, with helpers
 * for generation, per-frame orbital rotation, and lookups.
 */
export class SolarSystemManager {
  seed: number;
  star: Star;
  planets: Planet[];
  orbitRadii: number[];

  constructor(seed: number, maxPlanets?: number, maxMoons?: number, warps?: number) {
    const s = generateSystem({ seed, maxPlanets, maxMoons, warps });
    this.seed = s.seed;
    this.star = s.star;
    this.planets = s.planets;
    this.orbitRadii = s.orbitRadii;
  }

  /** Regenerate the whole system from a new seed (used on warp). */
  regenerate(seed: number, maxPlanets?: number, maxMoons?: number, warps?: number): void {
    const s = generateSystem({ seed, maxPlanets, maxMoons, warps });
    this.seed = s.seed;
    this.star = s.star;
    this.planets = s.planets;
    this.orbitRadii = s.orbitRadii;
  }

  /** Revolve all planets around the star. */
  update(dt: number): void {
    for (const p of this.planets) {
      p.angle += p.orbitalSpeed * dt;
      p.pos.set(Math.cos(p.angle) * p.distance, Math.sin(p.angle) * p.distance);
      for (const m of p.moons) m.angle += m.speed * dt;
    }
  }

  byId(id: string): Planet | null {
    return this.planets.find((p) => p.id === id) ?? null;
  }
}
